import { useState } from 'react';
import { Button } from '../../design-system/components/Button';
import { Card } from '../../design-system/components/Card';
import { Select } from '../../design-system/components/Select';
import { Spinner } from '../../design-system/components/Spinner';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import type { DashboardPeriod } from '../../shared/data/queryKeys';
import { useAuthStore } from '../auth/store';
import type { RankingMetric } from './api';
import { useRanking } from './hooks';

const METRIC_OPTIONS = [
  { value: 'meetings', label: 'Reuniões' },
  { value: 'followups', label: 'Follow-ups' },
  { value: 'avg_sentiment', label: 'Sentimento médio' },
] as const;

const PERIOD_OPTIONS = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
] as const;

export function RankingScreen() {
  const role = useAuthStore((s) => s.user?.role);
  const [metric, setMetric] = useState<RankingMetric>('meetings');
  const [period, setPeriod] = useState<DashboardPeriod>('30d');
  const [exportHint, setExportHint] = useState<string | null>(null);
  const query = useRanking(metric, period);

  if (role !== 'manager' && role !== 'admin') {
    return (
      <EmptyState
        title="Sem permissão"
        description="O ranking está disponível apenas para gestores."
      />
    );
  }

  return (
    <div className="dash-section">
      <div className="dash-section__header">
        <h1 className="dash-section__title">Ranking de closers</h1>
        <div className="dash-section__header">
          <Select
            label="Métrica"
            options={METRIC_OPTIONS}
            value={metric}
            onChange={(e) => setMetric(e.target.value as RankingMetric)}
          />
          <Select
            label="Período"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
          />
          <Button
            variant="ghost"
            onClick={() => setExportHint('Exportação CSV ainda não está disponível.')}
          >
            Exportar CSV
          </Button>
        </div>
      </div>
      {exportHint !== null ? (
        <p className="muted" role="status">
          {exportHint}
        </p>
      ) : null}

      {query.isPending ? (
        <div className="center">
          <Spinner label="Carregando ranking" />
        </div>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data.rows.length === 0 ? (
        <EmptyState title="Sem dados para o período" />
      ) : (
        <Card>
          <table className="dash-table" aria-label="Ranking de closers">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Closer</th>
                <th scope="col">Valor</th>
              </tr>
            </thead>
            <tbody>
              {query.data.rows.map((row) => (
                <tr key={row.user_id}>
                  <td>{row.rank}</td>
                  <td>{row.name}</td>
                  <td>{row.value.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
